import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../../../../../../../components/Common/UI/Dialog';
import { cn } from '../../../../../../../utils/styles';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../../components/Common/UI/Select';
import { Input } from '../../../../../../../components/Common/UI/Input';
import Separator from '../../../../../../../components/Common/UI/Separator';
import { Button } from '../../../../../../../components/Common/UI/Button';
import { AccountManagementSubRow, INotificationType } from '../../../columns';
import { INotifyModal } from '../index';
import Icons from '../../../../../../../components/Common/Icons/Icons';
import Checkbox from '../../../../../../../components/Common/UI/Checkbox';

interface IModalEditDelivery {
  handleSave: (payload: INotificationType) => void;
  open: boolean;
  handleOpen: (payload: INotifyModal) => void;
  data: AccountManagementSubRow;
}

export const notifyType: { key: string; label: INotificationType }[] = [
  { key: 'Email', label: 'Email' },
  { key: 'SMS', label: 'SMS' },
  { key: 'Email & SMS', label: 'Email & SMS' },
];

interface IState {
  notifyType: INotificationType;
  email: string;
  attachStatement: boolean;
  password: string;
  phone?: string;
}

const initialData: IState = {
  notifyType: 'Email',
  email: '',
  attachStatement: false,
  password: '',
  phone: '',
};

function ModalEditNotifications({ open, handleOpen, handleSave, data }: IModalEditDelivery) {
  const [state, setState] = useState<IState>(initialData);

  useEffect(() => {
    if (open) {
      setState({
        ...state,
        notifyType: data.notification,
        email: data.email,
        phone: data.phone,
      });
    }
  }, [open]);

  const handleOnSelect = (payload: INotificationType) => {
    setState({
      ...state,
      notifyType: payload,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={e => {
        setState({ ...state, attachStatement: false, password: '' });
        handleOpen({ open: e, type: 'edit' });
      }}
    >
      <DialogContent className={cn('max-h-[98%] max-w-[600px]')}>
        <DialogTitle className="w-full h-auto flex gap-[20px] items-center">
          <div className="bg-[#307BB2] p-1 rounded-lg">
            <Icons.UserAccount className="size-[24px] text-white" />
          </div>
          Edit Notification Type
        </DialogTitle>

        <div className="grid gap-[20px]">
          <Select onValueChange={e => handleOnSelect(e as INotificationType)} value={state.notifyType}>
            <SelectTrigger className="w-full" label="Notify Type">
              <SelectValue placeholder="Notify Type" />
            </SelectTrigger>
            <SelectContent>
              {notifyType.map(({ key, label }) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {state.notifyType !== 'SMS' ? (
            <Input
              label="Email Address*"
              placeholder="Email address"
              value={state.email}
              onChange={e => setState({ ...state, email: e.target.value })}
            />
          ) : null}
          {state.notifyType !== 'Email' ? (
            <Input
              prefix="+1"
              iconPosition="left"
              label="Phone Number*"
              placeholder="Phone number"
              value={state.phone}
              onChange={e => setState({ ...state, phone: e.target.value })}
            />
          ) : null}
          <div className="flex gap-2 mt-[10px] items-center">
            <Checkbox id="attachStatementModal" onCheckedChange={e => setState({ ...state, attachStatement: !!e })} />
            <label
              htmlFor="attachStatementModal"
              className="text-sm text-primary cursor-pointer font-medium peer-disabled:cursor-not-allowed
                  peer-disabled:opacity-70"
            >
              Attach Statement
            </label>
          </div>
          {state.attachStatement ? (
            <Input
              errorClassName="text-left"
              error={!state.password.length ? 'Password is required when attachment is selected' : null}
              label="Password*"
              placeholder="Attach statement password"
              value={state.password}
              onChange={e => setState({ ...state, password: e.target.value })}
            />
          ) : null}
        </div>
        {state.attachStatement && !state.password.length ? (
          <div className="mt-[2px] text-destructive text-[13px] text-right">
            Please correct error before submitting the form
          </div>
        ) : null}
        <Separator className="m-0" />
        <div className="flex justify-end gap-4">
          <Button
            variant="link"
            className="py-3 hover:no-underline"
            onClick={() => handleOpen({ open: false, type: 'edit' })}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            disabled={state.attachStatement && !state.password.length}
            className="py-3 !bg-addition5 hover:opacity-85 px-[20px]"
            onClick={() => {
              setState({ ...state, attachStatement: false, password: '' });
              handleSave(state.notifyType);
              handleOpen({ open: false, type: 'edit' });
            }}
          >
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ModalEditNotifications;
